import { ButtonContext } from '../../navigation/Button/Button';
import { Colors, type ColorName } from '../../../theme';
import clsx from 'clsx';
import { CSSProperties, ReactNode, useMemo } from 'react';
import styles from './Footer.module.scss';

type Neutrals = Extract<
  ColorName,
  | 'BackgroundWeaker'
  | 'BackgroundDefault'
  | 'BackgroundStronger'
  | 'BackgroundStrongest'
  | 'GrayWarm1'
  | 'GrayWarm2'
  | 'GrayWarm3'
  | 'GrayWarm4'
  | 'GrayWarm5'
  | 'GrayWarm6'
  | 'GrayWarm7'
  | 'GrayWarm8'
  | 'GrayWarm9'
  | 'GrayWarm10'
>;

type ModalContentFooterProps = {
  /**
   * The background color of the footer.
   * @default 'BackgroundDefault'
   */
  backgroundColor?: Neutrals;
  /** The children to render inside the component. */
  children: ReactNode;
  /** Optional property for overriding styles. */
  className?: string;
  /** The layout of the buttons in the footer. Only applies to desktop-sized screens. */
  justify?: 'end' | 'start' | 'space-between';
  /** Optional property for overriding styles. */
  style?: CSSProperties;
};

/**
 * The dialog footer, which should contain up to two buttons:
 * - an optional secondary button
 * - an required primary button
 */
export const ModalDialogFooter = ({
  backgroundColor = 'BackgroundDefault',
  children,
  className,
  justify = 'end',
  style,
}: ModalContentFooterProps) => {
  const contextValue = useMemo(() => ({ size: 'medium' as const }), []);

  return (
    <footer
      className={clsx(styles.modalDialogFooter, className)}
      data-zds
      data-justify={justify}
      style={{
        ...style,
        '--footer-background-color': Colors[backgroundColor],
      }}
    >
      <ButtonContext.Provider value={contextValue}>
        {children}
      </ButtonContext.Provider>
    </footer>
  );
};
